import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  Svg,
  Path,
  StyleSheet,
} from '@react-pdf/renderer';
import { LabelLayout, PackageRow } from '@/services/api/packages';
import {
  DASH_LOGO_PATHS,
  DASH_LOGO_RATIO,
  DASH_LOGO_VIEWBOX,
  LABEL_DOTTED,
  LABEL_FIELD,
  LABEL_INK,
  labelKey,
} from './labelConstants';

// Dash wordmark rendered as vector paths so it prints crisp + solid black on
// thermal media (no raster logo, no separate "DASH" text needed).
const DashWordmark: React.FC<{ height: number }> = ({ height }) => (
  <Svg
    viewBox={DASH_LOGO_VIEWBOX}
    width={height * DASH_LOGO_RATIO}
    height={height}
  >
    {DASH_LOGO_PATHS.map((d, i) => (
      <Path key={i} d={d} fill={LABEL_INK} />
    ))}
  </Svg>
);

// Client names vary wildly in length. Step the header font down for long names
// so they wrap onto a tidy second line instead of overflowing the label width.
const clientFontSize = (name: string, base: number, small: number): number =>
  name.length > 20 ? small : base;

// 1mm in PDF points (72dpi): 72 / 25.4
const MM = 72 / 25.4;
const LABEL_W = 100 * MM; // 100mm wide for all thermal
const LABEL_H_FULL = 150 * MM; // 100×150mm — thermal full (4×6 in)
const LABEL_H_SQ = 100 * MM; // 100×100mm — thermal square

const s = StyleSheet.create({
  box: {
    borderWidth: 1.5,
    borderColor: LABEL_INK,
    borderStyle: 'solid',
    borderRadius: 4,
    padding: 12,
    flexDirection: 'column',
    backgroundColor: '#ffffff',
  },
  top: {
    flexDirection: 'row',
    // Top-align so the wordmark sits with the client name's FIRST line instead
    // of floating in the vertical centre when the name wraps to two lines.
    alignItems: 'flex-start',
    borderBottomWidth: 2,
    borderBottomColor: LABEL_INK,
    borderBottomStyle: 'solid',
    paddingBottom: 8,
    marginBottom: 8,
  },
  logo: { width: 14, height: 14, marginRight: 5, objectFit: 'contain' },
  brand: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: LABEL_INK },
  // "| {clientName}" appended after the DASH wordmark in the header. marginTop
  // nudges the first line to the wordmark's optical centre; marginLeft gives the
  // pipe room to breathe from the wordmark.
  client: {
    flex: 1,
    marginLeft: 10,
    marginTop: 2.7,
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
  },
  qrWrap: {
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderBottomColor: LABEL_INK,
    borderBottomStyle: 'dashed',
    paddingTop: 10,
    paddingBottom: 10,
  },
  qr: { width: 108, height: 108, marginBottom: 4 },
  itemText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: LABEL_INK,
    textAlign: 'center',
    marginTop: 2,
  },
  party: {
    borderBottomWidth: 1,
    borderBottomColor: LABEL_DOTTED,
    borderBottomStyle: 'solid',
    paddingTop: 5,
    paddingBottom: 5,
  },
  partyLabel: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  partyName: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    marginTop: 1,
  },
  partyAddr: {
    fontSize: 8.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    marginTop: 2,
  },
  rows: { flexGrow: 1, paddingTop: 4 },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: LABEL_DOTTED,
    borderBottomStyle: 'solid',
    paddingTop: 4,
    paddingBottom: 4,
  },
  rowKey: {
    width: 80,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  rowVal: { flex: 1, fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: LABEL_INK },
  a4Page: { padding: 14, justifyContent: 'center', alignItems: 'center' },
});

// ─── Square thermal (100×100mm) styles — Astro-style layout ──────────────────
const sq = StyleSheet.create({
  box: {
    width: '100%',
    height: '100%',
    padding: 12,
    flexDirection: 'column',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    // Top-align so the wordmark sits with the client name's first line when it
    // wraps (see the full-label `top` style for the rationale).
    alignItems: 'flex-start',
    paddingBottom: 5,
    borderBottomWidth: 1.5,
    borderBottomColor: LABEL_INK,
    borderBottomStyle: 'solid',
  },
  // Name + X/Y badge row
  nameRow: { flexDirection: 'row', paddingTop: 7, paddingBottom: 7 },
  nameCol: { flex: 1, paddingRight: 6 },
  outletName: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    lineHeight: 1.1,
  },
  bookingId: {
    fontSize: 9,
    fontFamily: 'Courier-Bold',
    color: LABEL_INK,
    marginTop: 3,
  },
  badgeCol: { width: 86, alignItems: 'flex-end' },
  badge: {
    borderWidth: 1.5,
    borderColor: LABEL_INK,
    borderStyle: 'solid',
    borderRadius: 5,
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 8,
    paddingRight: 8,
  },
  badgeText: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: LABEL_INK },
  // "| {clientName}" appended after the DASH wordmark in the header. marginTop
  // nudges the first line to the wordmark's optical centre; marginLeft gives the
  // pipe room to breathe from the wordmark.
  client: {
    flex: 1,
    marginLeft: 10,
    marginTop: 2.3,
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
  },
  itemName: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    marginTop: 4,
    textAlign: 'right',
    lineHeight: 1.2,
  },
  // Body: address (left) + QR (right)
  body: {
    flexDirection: 'row',
    flexGrow: 1,
    borderTopWidth: 1,
    borderTopColor: LABEL_INK,
    borderTopStyle: 'solid',
    paddingTop: 6,
  },
  addrCol: { flex: 1, paddingRight: 8 },
  qrCol: { width: 104, alignItems: 'center' },
  qr: { width: 100, height: 100 },
  label: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  addrText: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    lineHeight: 1.25,
    marginTop: 1,
  },
  fromBlock: { marginTop: 6 },
  // Footer: waybill + qty/weight
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: LABEL_DOTTED,
    borderTopStyle: 'dashed',
    paddingTop: 5,
    marginTop: 6,
  },
  footRight: { alignItems: 'flex-end' },
  footKey: {
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
    color: LABEL_INK,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  footVal: {
    fontSize: 9,
    fontFamily: 'Courier-Bold',
    color: LABEL_INK,
    marginTop: 1,
  },
});

interface LabelBoxSquareProps {
  pkg: PackageRow;
  qr: string;
}

const LabelBoxSquare: React.FC<LabelBoxSquareProps> = ({ pkg, qr }) => (
  <View style={sq.box}>
    {/* Header — Dash wordmark · client name */}
    <View style={sq.header}>
      <DashWordmark height={15} />
      {pkg.client?.name ? (
        <View style={[sq.client, { flexDirection: 'row' }]}>
          <Text
            style={{
              marginRight: 6,
              fontFamily: 'Helvetica-Bold',
              color: LABEL_INK,
              fontSize: clientFontSize(pkg.client.name, 10, 8),
            }}
          >
            |
          </Text>
          <Text
            style={{
              flex: 1,
              fontFamily: 'Helvetica-Bold',
              color: LABEL_INK,
              fontSize: clientFontSize(pkg.client.name, 10, 8),
            }}
          >
            {pkg.client.name}
          </Text>
        </View>
      ) : null}
    </View>

    {/* Recipient name + booking ID (left) · X/Y badge + item name (right) */}
    <View style={sq.nameRow}>
      <View style={sq.nameCol}>
        <Text style={sq.outletName}>{pkg.outlet}</Text>
        {pkg.bookingId ? (
          <Text style={sq.bookingId}>#{pkg.bookingId}</Text>
        ) : null}
      </View>
      <View style={sq.badgeCol}>
        {pkg.itemTotal ? (
          <View style={sq.badge}>
            <Text style={sq.badgeText}>
              {pkg.itemSeq} / {pkg.itemTotal}
            </Text>
          </View>
        ) : null}
        {pkg.itemName ? <Text style={sq.itemName}>{pkg.itemName}</Text> : null}
      </View>
    </View>

    {/* Address (left) + QR (right) */}
    <View style={sq.body}>
      <View style={sq.addrCol}>
        <Text style={sq.label}>{LABEL_FIELD.to}</Text>
        {pkg.recipientAddress ? (
          <Text style={sq.addrText}>{pkg.recipientAddress}</Text>
        ) : null}
        <View style={sq.fromBlock}>
          <Text style={sq.label}>{LABEL_FIELD.from}</Text>
          <Text style={sq.addrText}>{pkg.sender}</Text>
          {pkg.senderAddress ? (
            <Text style={sq.addrText}>{pkg.senderAddress}</Text>
          ) : null}
        </View>
      </View>
      <View style={sq.qrCol}>
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <Image src={qr} style={sq.qr} />
      </View>
    </View>

    {/* Footer: waybill · qty/weight */}
    <View style={sq.footer}>
      {pkg.deliveryId ? (
        <View>
          <Text style={sq.footKey}>{LABEL_FIELD.waybill}</Text>
          <Text style={sq.footVal}>{pkg.deliveryId}</Text>
        </View>
      ) : null}
      <View style={sq.footRight}>
        <Text style={sq.footKey}>{LABEL_FIELD.qtyWeight}</Text>
        <Text style={sq.footVal}>
          {pkg.qty} pcs · {pkg.weight} kg
        </Text>
      </View>
    </View>
  </View>
);

interface LabelBoxProps {
  pkg: PackageRow;
  qr: string;
  logoUrl: string;
  fill?: boolean;
}

const LabelBox: React.FC<LabelBoxProps> = ({ pkg, qr, logoUrl, fill }) => (
  <View
    style={[
      s.box,
      fill
        ? { width: '100%', height: '100%' }
        : { width: LABEL_W, height: LABEL_H_FULL },
    ]}
  >
    <View style={s.top}>
      {fill ? (
        // Thermal full → vector wordmark (crisp black on thermal media).
        <DashWordmark height={18} />
      ) : (
        // A4 → keep the original colour logo lockup.
        <>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <Image src={logoUrl} style={s.logo} />
          <Text style={s.brand}>DASH</Text>
        </>
      )}
      {pkg.client?.name ? (
        // The thermal wordmark (18) and the A4 colour lockup (14 logo + 13 text)
        // have different optical centres, so the nudge that centres the client
        // name differs per branch.
        <View
          style={[s.client, { flexDirection: 'row', marginTop: fill ? 2.7 : 0.8 }]}
        >
          <Text
            style={{
              marginRight: 6,
              fontFamily: 'Helvetica-Bold',
              color: LABEL_INK,
              fontSize: clientFontSize(pkg.client.name, 11, 9),
            }}
          >
            |
          </Text>
          <Text
            style={{
              flex: 1,
              fontFamily: 'Helvetica-Bold',
              color: LABEL_INK,
              fontSize: clientFontSize(pkg.client.name, 11, 9),
            }}
          >
            {pkg.client.name}
          </Text>
        </View>
      ) : null}
    </View>

    <View style={s.qrWrap}>
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <Image src={qr} style={s.qr} />
      {pkg.itemName ? <Text style={s.itemText}>{pkg.itemName}</Text> : null}
    </View>

    <View style={s.party}>
      <Text style={s.partyLabel}>{LABEL_FIELD.from}</Text>
      <Text style={s.partyName}>{pkg.sender}</Text>
      {pkg.senderAddress ? (
        <Text style={s.partyAddr}>{pkg.senderAddress}</Text>
      ) : null}
    </View>

    <View style={s.party}>
      <Text style={s.partyLabel}>{LABEL_FIELD.to}</Text>
      <Text style={s.partyName}>{pkg.outlet}</Text>
      {pkg.recipientAddress ? (
        <Text style={s.partyAddr}>{pkg.recipientAddress}</Text>
      ) : null}
    </View>

    <View style={s.rows}>
      <View style={s.row}>
        <Text style={s.rowKey}>{LABEL_FIELD.qtyWeight}</Text>
        <Text style={s.rowVal}>
          {pkg.qty} pcs · {pkg.weight} kg
        </Text>
      </View>
      {pkg.deliveryId ? (
        <View style={s.row}>
          <Text style={s.rowKey}>{LABEL_FIELD.waybill}</Text>
          <Text style={[s.rowVal, { fontFamily: 'Courier-Bold' }]}>{pkg.deliveryId}</Text>
        </View>
      ) : null}
      {pkg.bookingId ? (
        <View style={s.row}>
          <Text style={s.rowKey}>{LABEL_FIELD.bookingId}</Text>
          <Text style={[s.rowVal, { fontFamily: 'Courier-Bold' }]}>{pkg.bookingId}</Text>
        </View>
      ) : null}
    </View>
  </View>
);

export interface LabelDocumentProps {
  packages: PackageRow[];
  layout: LabelLayout;
  qrMap: Record<string, string>;
  logoUrl: string;
}

export const LabelDocument: React.FC<LabelDocumentProps> = ({
  packages,
  layout,
  qrMap,
  logoUrl,
}) => {
  if (layout === 'A4_2UP') {
    return (
      <Document>
        {packages.map((pkg) => (
          <Page key={labelKey(pkg)} size="A4" style={s.a4Page}>
            <LabelBox pkg={pkg} qr={qrMap[labelKey(pkg)]} logoUrl={logoUrl} />
          </Page>
        ))}
      </Document>
    );
  }

  if (layout === 'THERMAL_100X100') {
    return (
      <Document>
        {packages.map((pkg) => (
          <Page key={labelKey(pkg)} size={{ width: LABEL_W, height: LABEL_H_SQ }}>
            <LabelBoxSquare pkg={pkg} qr={qrMap[labelKey(pkg)]} />
          </Page>
        ))}
      </Document>
    );
  }

  return (
    <Document>
      {packages.map((pkg) => (
        <Page key={labelKey(pkg)} size={{ width: LABEL_W, height: LABEL_H_FULL }}>
          <LabelBox pkg={pkg} qr={qrMap[labelKey(pkg)]} logoUrl={logoUrl} fill />
        </Page>
      ))}
    </Document>
  );
};
